import React, { useState, useEffect, useCallback } from 'react';
import { Download, Copy, Check, Info, X, Tv, Monitor, Smartphone } from 'lucide-react';

const API_URL = '';

// Logo Alpha Tecnologia (Lobo)
const ALPHA_LOGO_URL = 'https://customer-assets-v7afamib.emergentagent.net/job_81a10ac5-4362-40b8-978c-4a083e804485/artifacts/zibp9kbv_9BC204D8-B527-460C-B850-55FF7E3933B7.png';

// URLs das logos dos apps
const APP_LOGOS = {
  'xciptv': 'https://customer-assets.emergentagent.com/job_81a10ac5-4362-40b8-978c-4a083e804485/artifacts/eku1ncvt_IMG_5380.jpeg',
  'vizzion-play': 'https://customer-assets.emergentagent.com/job_81a10ac5-4362-40b8-978c-4a083e804485/artifacts/h2rbdx6i_IMG_5379.jpeg',
  'brasil-iptv': 'https://customer-assets.emergentagent.com/job_81a10ac5-4362-40b8-978c-4a083e804485/artifacts/y1zkyrdc_IMG_5377.png',
  'xcloud-tv': 'https://customer-assets.emergentagent.com/job_81a10ac5-4362-40b8-978c-4a083e804485/artifacts/n3vedcro_IMG_5378.png',
  'iptv-smarters': 'https://customer-assets.emergentagent.com/job_81a10ac5-4362-40b8-978c-4a083e804485/artifacts/gfgod1o9_download.png',
  'cartec': 'https://customer-assets.emergentagent.com/job_salebotflow/artifacts/yxgkf7f7_IMG_4355.png',
};

const NAMES = {
  'xciptv': 'XCIPTV',
  'vizzion-play': 'Vizzion Play',
  'brasil-iptv': 'Brasil IPTV',
  'xcloud-tv': 'Xcloud TV',
  'iptv-smarters': 'IPTV Smarters Pro',
};

const AlphaPage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => { fetchApps(); }, []);

  const fetchApps = async () => {
    try {
      const res = await fetch(`${API_URL}/api/apps`);
      if (res.ok) {
        const data = await res.json();
        // Filtrar: remover MN Tech e apps de outros revendedores
        const alphaApps = (data.apps || []).filter(app => 
          app.slug !== 'mn-tech' && 
          app.slug !== 'cartec' &&
          !app.owner
        );
        setApps(alphaApps.sort((a, b) => a.order - b.order));
      }
    } catch (err) {
      console.error('Erro ao carregar apps:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (showInstructions) {
      if (['Escape', 'Backspace', 'Enter'].includes(e.key)) {
        setShowInstructions(false);
        e.preventDefault();
      }
      return;
    }
    const total = apps.length;
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedIndex(p => Math.max(0, p - 1));
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        setFocusedIndex(p => Math.min(total - 1, p + 1));
        break;
      case 'Enter':
      case 'Select':
        e.preventDefault();
        const btn = document.querySelector(`[data-alpha-focus="${focusedIndex}"] [data-testid^="alpha-download"]`);
        if (btn) btn.click();
        break;
      default:
        break;
    }
  }, [apps.length, focusedIndex, showInstructions]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const doDownload = (app) => {
    if (!app.apk_filename) return;
    setDownloading(app.slug);
    const link = document.createElement('a');
    link.href = `${API_URL}/api/download/${encodeURIComponent(app.apk_filename)}`;
    link.download = '';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setDownloading(null), 3000);
  };

  const doCopy = async (code) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.cssText = 'position:fixed;left:-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch { /* silent */ }
  };

  const getName = (app) => NAMES[app.slug] || app.name;
  const getIcon = (app) => APP_LOGOS[app.slug] || app.icon_url || ALPHA_LOGO_URL;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <p style={{ color: '#ff0000', fontSize: 18, fontWeight: 600 }}>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="alpha-page">
      {/* HEADER - Lobo metálico */}
      <header className="alpha-header">
        <div className="alpha-header-content">
          <img src={ALPHA_LOGO_URL} alt="Alpha Tecnologia" className="alpha-logo" />
          <button
            data-testid="alpha-instructions-btn"
            onClick={() => setShowInstructions(true)}
            className="alpha-help-btn"
          >
            <Info size={18} />
            <span>Como Instalar</span>
          </button>
        </div>
      </header>

      {/* Dispositivos compatíveis */}
      <section className="alpha-devices">
        <div className="alpha-devices-row">
          <div className="alpha-device">
            <Tv size={28} />
            <span>TV Box</span>
          </div>
          <div className="alpha-device">
            <Monitor size={28} />
            <span>Smart TV</span>
          </div>
          <div className="alpha-device">
            <Smartphone size={28} />
            <span>Android</span>
          </div>
        </div>
        <p className="alpha-nav-hint">
          Use <span className="alpha-key">setas</span> para navegar | <span className="alpha-key">OK</span> para baixar
        </p>
      </section>

      {/* LISTA DE APPS */}
      <main className="alpha-main">
        <div className="alpha-grid">
          {apps.map((app, idx) => {
            const focused = focusedIndex === idx;
            const hasCode = app.downloader_code?.trim();
            return (
              <div
                key={app.slug}
                data-testid={`alpha-app-card-${app.slug}`}
                data-alpha-focus={idx}
                tabIndex={0}
                onFocus={() => setFocusedIndex(idx)}
                className={`alpha-card ${focused ? 'focused' : ''}`}
              >
                {/* Ícone do app */}
                <div className="alpha-card-icon">
                  <img
                    src={getIcon(app)}
                    alt={getName(app)}
                    loading="lazy"
                    onError={(e) => { e.target.src = ALPHA_LOGO_URL; }}
                  />
                </div>

                {/* Info */}
                <div className="alpha-card-info">
                  <h3 className="alpha-card-name">{getName(app)}</h3>
                  
                  {/* Botões */}
                  <div className="alpha-card-actions">
                    <button
                      data-testid={`alpha-download-${app.slug}`}
                      onClick={(e) => { e.stopPropagation(); doDownload(app); }}
                      className={`alpha-dl-btn ${downloading === app.slug ? 'active' : ''}`}
                    >
                      <Download size={18} />
                      {downloading === app.slug ? 'BAIXANDO...' : 'BAIXAR'}
                    </button>

                    {hasCode && (
                      <button
                        data-testid={`alpha-copy-${app.slug}`}
                        onClick={(e) => { e.stopPropagation(); doCopy(app.downloader_code); }}
                        className={`alpha-code-btn ${copiedCode === app.downloader_code ? 'copied' : ''}`}
                      >
                        {copiedCode === app.downloader_code ? <Check size={16} /> : <Copy size={16} />}
                        {copiedCode === app.downloader_code ? 'Copiado!' : `Downloader: ${app.downloader_code}`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="alpha-footer">
        <img src={ALPHA_LOGO_URL} alt="Alpha" className="alpha-footer-logo" />
        <p>2025 Alpha Tecnologia - Todos os direitos reservados</p>
      </footer>

      {/* MODAL INSTRUÇÕES */}
      {showInstructions && (
        <div className="alpha-overlay" onClick={() => setShowInstructions(false)}>
          <div className="alpha-modal" onClick={e => e.stopPropagation()}>
            <div className="alpha-modal-header">
              <h2><Info size={22} /> Como Instalar</h2>
              <button onClick={() => setShowInstructions(false)} className="alpha-modal-close">
                <X size={20} />
              </button>
            </div>

            <div className="alpha-steps">
              {[
                { n: 1, t: 'Baixe o Downloader', d: 'Na Play Store da sua TV, busque e instale o app "Downloader"' },
                { n: 2, t: 'Digite o Código', d: 'Abra o Downloader e digite o código numérico do app desejado' },
                { n: 3, t: 'Instale o APK', d: 'O download começa automaticamente. Clique em "Instalar"' },
                { n: 4, t: 'Permita Instalação', d: 'Se pedir, vá em Configurações, Segurança e Fontes desconhecidas' }
              ].map(step => (
                <div key={step.n} className="alpha-step">
                  <div className="alpha-step-num">{step.n}</div>
                  <div>
                    <p className="alpha-step-title">{step.t}</p>
                    <p className="alpha-step-desc">{step.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowInstructions(false)}
              autoFocus
              data-testid="alpha-instructions-close"
              className="alpha-modal-btn"
            >
              ENTENDI
            </button>
          </div>
        </div>
      )}

      {/* ESTILOS - CSS compatível com TVs antigas (sem backdrop-filter, rgba, radial-gradient) */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .alpha-page {
          min-height: 100vh;
          background-color: #000000;
          color: #c0c0c0;
          font-family: 'Inter', -apple-system, sans-serif;
        }

        /* ===== HEADER ===== */
        .alpha-header {
          position: sticky;
          top: 0;
          z-index: 100;
          background-color: #000000;
          border-bottom: 2px solid #1a1a1a;
          padding: 12px 20px;
        }
        .alpha-header-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .alpha-logo {
          height: 70px;
          object-fit: contain;
        }
        .alpha-help-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background-color: #1a1a1a;
          border: 2px solid #333333;
          border-radius: 12px;
          color: #ff0000;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .alpha-help-btn:hover,
        .alpha-help-btn:focus {
          border-color: #ff0000;
          outline: none;
        }

        /* ===== DISPOSITIVOS ===== */
        .alpha-devices {
          padding: 20px;
          border-bottom: 1px solid #1a1a1a;
          text-align: center;
          background-color: #0a0a0a;
        }
        .alpha-devices-row {
          display: flex;
          justify-content: center;
          gap: 40px;
          margin-bottom: 12px;
        }
        .alpha-device {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          color: #ff0000;
          font-size: 13px;
          font-weight: 500;
        }
        .alpha-nav-hint {
          color: #505050;
          font-size: 14px;
        }
        .alpha-key {
          color: #c0c0c0;
          font-weight: 600;
        }

        /* ===== MAIN ===== */
        .alpha-main {
          max-width: 1400px;
          margin: 0 auto;
          padding: 30px 20px 60px;
        }

        /* ===== GRID ===== */
        .alpha-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* ===== CARD ===== */
        .alpha-card {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px 24px;
          background-color: #0a0a0a;
          border: 2px solid #1a1a1a;
          border-radius: 16px;
          cursor: pointer;
          transition: border-color 0.2s, transform 0.2s;
        }
        .alpha-card:hover {
          border-color: #333333;
        }
        .alpha-card.focused {
          border-color: #ff0000;
          box-shadow: 0 0 30px #330000;
          transform: scale(1.01);
        }
        .alpha-card:focus {
          outline: none;
          border-color: #ff0000;
        }

        .alpha-card-icon {
          flex-shrink: 0;
          width: 80px;
          height: 80px;
          border-radius: 16px;
          overflow: hidden;
          border: 2px solid #1a1a1a;
          background-color: #000000;
        }
        .alpha-card-icon img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .alpha-card-info {
          flex: 1;
          min-width: 0;
        }
        .alpha-card-name {
          font-size: 22px;
          font-weight: 700;
          color: #e0e0e0;
          margin-bottom: 12px;
        }
        .alpha-card-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        /* ===== BOTÕES ===== */
        .alpha-dl-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background-color: #990000;
          border: none;
          border-radius: 10px;
          color: #ffffff;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .alpha-dl-btn:hover,
        .alpha-dl-btn:focus {
          background-color: #cc0000;
          outline: none;
        }
        .alpha-dl-btn.active {
          background-color: #166534;
        }

        .alpha-code-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 12px 20px;
          background-color: #b45309;
          border: none;
          border-radius: 10px;
          color: #ffffff;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
          white-space: nowrap;
        }
        .alpha-code-btn:hover,
        .alpha-code-btn:focus {
          background-color: #d97706;
          outline: none;
        }
        .alpha-code-btn.copied {
          background-color: #166534;
        }

        /* ===== FOOTER ===== */
        .alpha-footer {
          background-color: #050505;
          border-top: 2px solid #1a1a1a;
          padding: 40px 20px;
          text-align: center;
        }
        .alpha-footer-logo {
          height: 50px;
          margin-bottom: 16px;
          opacity: 0.7;
        }
        .alpha-footer p {
          color: #333333;
          font-size: 13px;
        }

        /* ===== MODAL ===== */
        .alpha-overlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background-color: #000000;
        }
        .alpha-modal {
          background-color: #0a0a0a;
          border: 2px solid #1a1a1a;
          border-radius: 20px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow: auto;
          padding: 28px;
        }
        .alpha-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
        }
        .alpha-modal-header h2 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 22px;
          font-weight: 800;
          color: #ff0000;
          margin: 0;
        }
        .alpha-modal-close {
          padding: 8px;
          background-color: #1a1a1a;
          border: none;
          border-radius: 8px;
          color: #666666;
          cursor: pointer;
        }
        .alpha-modal-close:hover,
        .alpha-modal-close:focus {
          color: #ff0000;
          outline: none;
        }

        .alpha-steps {
          margin-bottom: 24px;
        }
        .alpha-step {
          display: flex;
          gap: 14px;
          margin-bottom: 20px;
          align-items: flex-start;
        }
        .alpha-step-num {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          background-color: #990000;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          color: #ffffff;
        }
        .alpha-step-title {
          font-size: 16px;
          font-weight: 700;
          color: #e0e0e0;
          margin-bottom: 4px;
        }
        .alpha-step-desc {
          font-size: 14px;
          color: #666666;
        }

        .alpha-modal-btn {
          width: 100%;
          padding: 16px;
          background-color: #990000;
          border: none;
          border-radius: 12px;
          color: #ffffff;
          font-size: 18px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .alpha-modal-btn:hover,
        .alpha-modal-btn:focus {
          background-color: #cc0000;
          outline: none;
        }

        /* Focus states para TV */
        [data-alpha-focus]:focus {
          outline: none;
        }

        /* ===== RESPONSIVO - TV / DESKTOP ===== */
        @media (min-width: 768px) {
          .alpha-header {
            padding: 16px 40px;
          }
          .alpha-logo {
            height: 90px;
          }
          .alpha-help-btn {
            padding: 14px 28px;
            font-size: 16px;
          }

          .alpha-main {
            padding: 40px;
          }

          .alpha-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
          }

          .alpha-card {
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 32px;
          }
          .alpha-card-icon {
            width: 100px;
            height: 100px;
            margin-bottom: 8px;
          }
          .alpha-card-name {
            font-size: 24px;
          }
          .alpha-card-actions {
            justify-content: center;
            flex-direction: column;
            width: 100%;
          }
          .alpha-dl-btn {
            width: 100%;
            justify-content: center;
            padding: 16px 32px;
            font-size: 18px;
          }
          .alpha-code-btn {
            width: 100%;
            justify-content: center;
            padding: 14px 24px;
            font-size: 16px;
          }

          .alpha-footer {
            padding: 50px 40px;
          }
          .alpha-footer-logo {
            height: 60px;
          }
        }

        @media (min-width: 1200px) {
          .alpha-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          .alpha-logo {
            height: 100px;
          }
        }

        /* Animação de entrada */
        .alpha-card {
          animation: alphaFadeIn 0.3s ease both;
        }
        .alpha-card:nth-child(1) { animation-delay: 0.05s; }
        .alpha-card:nth-child(2) { animation-delay: 0.1s; }
        .alpha-card:nth-child(3) { animation-delay: 0.15s; }
        .alpha-card:nth-child(4) { animation-delay: 0.2s; }
        .alpha-card:nth-child(5) { animation-delay: 0.25s; }
        .alpha-card:nth-child(6) { animation-delay: 0.3s; }

        @keyframes alphaFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AlphaPage;
